import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../../pages/Personal Attendance/useAuth";
import { WORKFLOWS } from "./MenuHub";
import api from "../../api";
import { useEmployeePhoto } from "../../utils/employeePhoto";

/**
 * CommandPalette — Round 25. Press Ctrl+K (or Cmd+K) anywhere, type a few
 * letters, Enter — jump straight to any workflow, sub-action, department,
 * or HR workspace. The topbar search button dispatches `ssw:openPalette`.
 *
 * Permission model: entries are filtered with the SAME gates the launcher
 * and sidebar use (module gates via canAccessModule, manager/HR/IT flags),
 * so the palette can never reveal a destination the user couldn't reach
 * through the UI. Workflow entries come straight from MenuHub's catalog
 * (single source of truth); department entries mirror LayoutMenuData's
 * gates and are documented as such.
 *
 * Pages are pure frontend: navigation only.
 *
 * Round DIR-1 (2026-10-03, owner) — "find a colleague": typing two or more
 * letters also lists matching people (name, number, title or department).
 * Choosing one shows their work e-mail, desk extension and company cell with
 * the photo, so you can mail or call without opening the org chart. The list
 * comes from GET api/OrgChart/directory (same people, same company contact
 * fields and same "Org Chart - Open" gate as the chart; never the personal
 * phone), loaded once when the search first opens and kept for 30 minutes.
 */

const DIRECTORY_TTL_MS = 30 * 60 * 1000;
let directoryCache = null; // { at, people, deptNames } for this page load

async function loadDirectory() {
    if (directoryCache && Date.now() - directoryCache.at < DIRECTORY_TTL_MS) return directoryCache;
    const [dir, dep] = await Promise.all([
        api.get("OrgChart/directory"),
        api.get("Department").catch(() => ({ data: [] })),
    ]);
    const deptNames = {};
    const list = dep?.data?.value ?? dep?.data ?? [];
    (Array.isArray(list) ? list : []).forEach((d) => {
        const code = String(d.jcDept1 ?? d.JcDept1 ?? "").trim();
        if (code) deptNames[code] = d.description ?? d.Description ?? code;
    });
    directoryCache = { at: Date.now(), people: Array.isArray(dir?.data) ? dir.data : [], deptNames };
    return directoryCache;
}

// Best matches first: name starts with the text, then a word of the name does,
// then the text is anywhere in the name, then number / title / department.
function rankPerson(p, q, words, deptName) {
    const name = String(p.name || "").toLowerCase();
    const rest = `${p.empId || ""} ${p.title || ""} ${deptName}`.toLowerCase();
    if (!words.every((w) => name.includes(w) || rest.includes(w))) return -1;
    if (name.startsWith(q)) return 0;
    if (name.split(/\s+/).some((part) => part.startsWith(words[0]))) return 1;
    if (name.includes(q)) return 2;
    return 3;
}

const telHref = (n) => `tel:${String(n || "").replace(/[^\d+]/g, "")}`;

const initialsOf = (name) =>
    String(name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";

const PersonCard = ({ person, deptName, onBack }) => {
    const { src } = useEmployeePhoto(person.empId);
    // No photo on file (or it cannot load): show the person's initials instead.
    const [photoOk, setPhotoOk] = useState(true);
    useEffect(() => { setPhotoOk(true); }, [src]);
    const [copied, setCopied] = useState(false);
    const copy = () => {
        if (!person.email || !navigator.clipboard) return;
        navigator.clipboard.writeText(person.email).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => {});
    };
    const row = { display: "flex", alignItems: "center", gap: 10, padding: "8px 4px", fontSize: 14, color: "var(--ssw-text-1, #1c2333)" };
    const icon = { fontSize: 17, color: "var(--ssw-text-2, #66708a)", width: 20, textAlign: "center" };
    const link = { color: "var(--ssw-accent-ink, #4338ca)", textDecoration: "none", fontWeight: 600 };
    return (
        <div style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10 }}>
                {photoOk ? (
                    <img src={src} onError={() => setPhotoOk(false)} alt="" width={56} height={56}
                         style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", flexShrink: 0,
                                  background: "var(--ssw-surface-3, #f3f6f9)" }} />
                ) : (
                    <div aria-hidden="true"
                         style={{ width: 56, height: 56, borderRadius: "50%", flexShrink: 0, display: "flex",
                                  alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 19,
                                  background: "var(--ssw-surface-3, #eef0f7)", color: "var(--ssw-accent-ink, #4338ca)" }}>
                        {initialsOf(person.name)}
                    </div>
                )}
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "var(--ssw-text-1, #1c2333)" }}>{person.name}</div>
                    <div style={{ fontSize: 13, color: "var(--ssw-text-2, #66708a)" }}>
                        {[person.title, deptName].filter(Boolean).join(" · ") || "\u00A0"}
                    </div>
                    {person.managerName ? (
                        <div style={{ fontSize: 12.5, color: "var(--ssw-text-2, #66708a)" }}>Reports to {person.managerName}</div>
                    ) : null}
                </div>
            </div>
            {person.email ? (
                <div style={row}>
                    <i className="ri-mail-line" style={icon} aria-hidden="true" />
                    <a href={`mailto:${person.email}`} style={link}>{person.email}</a>
                    <button type="button" onClick={copy} className="btn btn-sm btn-light ms-auto" style={{ fontSize: 12 }}>
                        {copied ? "Copied" : "Copy"}
                    </button>
                </div>
            ) : null}
            {person.extension ? (
                <div style={row}>
                    <i className="ri-phone-line" style={icon} aria-hidden="true" />
                    <span>Desk extension {person.extension}</span>
                </div>
            ) : null}
            {person.cell ? (
                <div style={row}>
                    <i className="ri-smartphone-line" style={icon} aria-hidden="true" />
                    <a href={telHref(person.cell)} style={link}>{person.cell}</a>
                    <span style={{ fontSize: 12, color: "var(--ssw-text-2, #66708a)" }}>company cell</span>
                </div>
            ) : null}
            {!person.email && !person.extension && !person.cell ? (
                <div style={{ ...row, color: "var(--ssw-text-2, #66708a)" }}>No work contact details on file.</div>
            ) : null}
            <div style={{ marginTop: 10 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={onBack}>
                    <i className="ri-arrow-left-line align-bottom me-1" aria-hidden="true" />Back to results
                </button>
            </div>
        </div>
    );
};

// Departments — labels/links/gates mirror Layouts/LayoutMenuData.js.
// Go-live parity (2026-09-24): the palette used role flags (manager/HR/IT)
// here, while the sidebar and the route guards use the server-resolved module
// keys — so it offered pages that ended on "access denied" and hid pages the
// user could open. Entries now carry the SAME module key / gate as the
// sidebar and the route in allRoutes.js.
const DEPARTMENTS = [
    { label: "Engineering",            to: "/engineering",   module: "dept.engineering" },
    { label: "Planning",               to: "/planning",      module: "dept.planning" },
    { label: "Production",             to: "/production",    module: "dept.production" },
    { label: "Quality",                to: "/quality",       module: "dept.quality" },
    { label: "Shipping",               to: "/shipping",      module: "dept.shipping" },
    { label: "Accounting",             to: "/accounting",    module: "dept.accounting" },
    { label: "Sales",                  to: "/sales",         module: "dept.sales" },
    { label: "Shukla",                 to: "/shukla",        module: "dept.shukla" },
    { label: "Human Resource",         to: "/humanresource", gate: "hr" },
    { label: "HR Dashboard",           to: "/hr/dashboard",  gate: "hrMenu" },
    { label: "My Profile",             to: "/profile/me" },
    { label: "Information Technology", to: "/it",            module: "dept.it" },
    { label: "Supervisor",             to: "/supervisor",    module: "dept.supervisor" },
    { label: "KPIs",                   to: "/kpi",           gate: "kpi" },
];

// Go-live parity (2026-09-24): the HR shell reads ?section= (the old ?tab=
// numbers are only normalised as a fallback), so link to the real sections.
const HR_WORKSPACES = [
    { label: "Employee management", to: "/humanresource?section=employees", gate: "hr" },
    { label: "HR reports",          to: "/humanresource?section=reports", gate: "hr" },
    { label: "Punch editor / Missing punches", to: "/humanresource?section=punches", gate: "hr" },
    { label: "Web forms",           to: "/humanresource?section=webforms", gate: "hr" },
];

// Same KPI permission list as KpiHubRouteGuard / the sidebar KPIs entry.
const KPI_PERMS = [
    "KPI - View Hub", "KPI - Finance Reports", "KPI - Sales Reports",
    "KPI - Inventory Reports", "KPI - Orders Reports", "KPI - Quality Reports",
    "KPI - Vendor Reports", "KPI - Planning Reports", "KPI - X-Ray Report",
    "KPI - Aged Receivables", "KPI - Aged Payables", "KPI - Variance Report",
];

const APPS = [
    { label: "MAFIA Aux",      to: "/mafia", module: "app.mafia" },
    { label: "Display Boards", to: "/display-boards", module: "app.displayboard" },
    { label: "Support",        to: "/support", module: "support" },
    { label: "Admin tools",    to: "/admin", gate: "admin" },
    { label: "Expected Visitors", to: "/visitors", module: "app.visitors" },
    { label: "Dashboard home", to: "/dashboard" },
    { label: "Workflows",      to: "/workflows" },
];

const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const navigate = useNavigate();

    const auth = useAuth();
    const { isManager, isItAdmin, isHR, isSuperUser, canAccessModule, hasAccess } = auth;

    // People search: same gate as the org chart (the server checks it too).
    const canSearchPeople = !!(isItAdmin || canAccessModule("orgchart.open"));
    const [directory, setDirectory] = useState(null);
    const [person, setPerson] = useState(null);

    const gateOk = useCallback((g) => {
        if (!g) return true;
        if (g === "mgr")   return !!(isManager || isItAdmin);
        if (g === "mgrHr") return !!(isManager || isHR || isItAdmin);
        if (g === "mgrSu") return !!(isManager || isSuperUser || isItAdmin);
        if (g === "hr")    return !!(isHR || isItAdmin);
        if (g === "it")    return !!isItAdmin;
        // Go-live parity (2026-09-24): the gates the routes actually use.
        if (g === "hrMenu") return !!(isItAdmin || (hasAccess && hasAccess("HR Menu")));
        if (g === "kpi")   return !!(isItAdmin || canAccessModule("dept.kpi") ||
                                     (hasAccess && KPI_PERMS.some((p) => hasAccess(p))));
        if (g === "admin") return !!(isItAdmin || canAccessModule("admin.hub"));
        return true;
    }, [isManager, isItAdmin, isHR, isSuperUser, canAccessModule, hasAccess]);

    const wfVisible = useCallback((e) => {
        if (e.module) return canAccessModule(e.module);
        if (e.requiresManagerOrAdmin) return !!(isManager || isItAdmin || isSuperUser || isHR);
        return true;
        // eslint pacification not needed: plain function
    }, [canAccessModule, isManager, isItAdmin, isSuperUser, isHR]);

    // Build the catalog once per auth change: workflows + flattened
    // sub-actions ("EPO › Ready for Payment"), departments, HR, apps.
    const catalog = useMemo(() => {
        const out = [];
        WORKFLOWS.forEach((wf) => {
            if (!wfVisible(wf)) return;
            const subs = (wf.subActions || []).filter(wfVisible);
            if (subs.length > 0) {
                out.push({ label: wf.label, hint: "Workflow hub", to: `/workflows?wf=${wf.key}`, icon: wf.icon });
                subs.forEach((sa) =>
                    out.push({ label: `${wf.label} › ${sa.label}`, hint: sa.subtitle, to: sa.to, icon: sa.icon })
                );
            } else {
                out.push({ label: wf.label, hint: wf.subtitle, to: wf.to, icon: wf.icon });
            }
        });
        DEPARTMENTS.forEach((d) => (d.module ? (isItAdmin || canAccessModule(d.module)) : gateOk(d.gate)) && out.push({ label: d.label, hint: "Department", to: d.to, icon: "ri-building-line" }));
        HR_WORKSPACES.forEach((h) => gateOk(h.gate) && out.push({ label: `HR › ${h.label}`, hint: "HR workspace", to: h.to, icon: "ri-team-line" }));
        APPS.forEach((a) => {
            if (a.module ? (isItAdmin || canAccessModule(a.module)) : gateOk(a.gate)) {
                out.push({ label: a.label, hint: "App", to: a.to, icon: "ri-apps-2-line" });
            }
        });
        return out;
    }, [wfVisible, gateOk, canAccessModule, isItAdmin]);

    const deptNameOf = useCallback((code) => {
        const c = String(code || "").trim();
        return (directory?.deptNames && directory.deptNames[c]) || c;
    }, [directory]);

    const people = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (q.length < 2 || !directory?.people) return [];
        const words = q.split(/\s+/).filter(Boolean);
        return directory.people
            .map((p) => ({ p, r: rankPerson(p, q, words, String(deptNameOf(p.department)).toLowerCase()) }))
            .filter((x) => x.r >= 0)
            .sort((a, b) => a.r - b.r || String(a.p.name).localeCompare(String(b.p.name)))
            .slice(0, 6)
            .map((x) => x.p);
    }, [query, directory, deptNameOf]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return catalog.slice(0, 9);
        const pages = catalog
            .filter((e) => e.label.toLowerCase().includes(q) || (e.hint || "").toLowerCase().includes(q))
            .slice(0, people.length ? 6 : 9);
        return [
            ...pages,
            ...people.map((p) => ({
                person: p,
                label: p.name,
                hint: [p.title, deptNameOf(p.department)].filter(Boolean).join(" · "),
                icon: "ri-user-3-line",
            })),
        ];
    }, [catalog, query, people, deptNameOf]);

    const close = useCallback(() => { setOpen(false); setQuery(""); setCursor(0); setPerson(null); }, []);

    const go = useCallback((entry) => {
        if (!entry) return;
        if (entry.person) { setPerson(entry.person); return; }
        close();
        navigate(entry.to);
    }, [navigate, close]);

    // The people list is loaded the first time the search opens.
    useEffect(() => {
        if (!open || !canSearchPeople || directory) return;
        let alive = true;
        loadDirectory().then((d) => { if (alive) setDirectory(d); }).catch(() => {});
        return () => { alive = false; };
    }, [open, canSearchPeople, directory]);

    // Global listeners: Ctrl/Cmd+K toggles; topbar button dispatches event.
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && String(e.key).toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
            } else if (e.key === "Escape") {
                setOpen(false);
            }
        };
        const onOpenEvt = () => setOpen(true);
        window.addEventListener("keydown", onKey);
        window.addEventListener("ssw:openPalette", onOpenEvt);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("ssw:openPalette", onOpenEvt);
        };
    }, []);

    useEffect(() => { if (open) setTimeout(() => inputRef.current && inputRef.current.focus(), 30); }, [open]);
    // However it was closed (Esc, Ctrl+K, a click outside), it opens fresh next time.
    useEffect(() => { if (!open) { setQuery(""); setCursor(0); setPerson(null); } }, [open]);
    useEffect(() => { setCursor(0); }, [query]);

    if (!open) return null;

    const onInputKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); go(results[cursor]); }
    };

    return (
        <div
            onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
            style={{
                position: "fixed", inset: 0, zIndex: 4000,
                background: "rgba(10, 12, 20, 0.45)",
                display: "flex", alignItems: "flex-start", justifyContent: "center",
                paddingTop: "12vh",
            }}
            role="dialog" aria-modal="true" aria-label="Quick navigation"
        >
            <div
                style={{
                    width: "min(640px, 92vw)",
                    background: "var(--ssw-surface, #fff)",
                    border: "1px solid var(--ssw-border, #e9ecf3)",
                    borderRadius: 14,
                    boxShadow: "0 24px 64px rgba(8, 10, 20, 0.35)",
                    overflow: "hidden",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: "1px solid var(--ssw-border, #e9ecf3)" }}>
                    <i className="ri-search-line" style={{ fontSize: 18, color: "var(--ssw-text-2, #66708a)" }} />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPerson(null); }}
                        onKeyDown={onInputKeyDown}
                        placeholder={canSearchPeople ? "Jump to a page or find a colleague…" : "Jump to… (try 'punch', 'travel', 'epo')"}
                        aria-label={canSearchPeople ? "Search pages and people" : "Search destinations"}
                        style={{
                            flex: 1, border: "none", outline: "none", background: "transparent",
                            fontSize: 15.5, color: "var(--ssw-text-1, #1c2333)",
                        }}
                    />
                    <span style={{ fontSize: 11, color: "var(--ssw-text-2, #66708a)", border: "1px solid var(--ssw-border, #e9ecf3)", borderRadius: 6, padding: "2px 6px" }}>Esc</span>
                </div>

                {person ? (
                    <PersonCard person={person} deptName={deptNameOf(person.department)} onBack={() => setPerson(null)} />
                ) : (
                <div ref={listRef} style={{ maxHeight: 380, overflowY: "auto", padding: 6 }}>
                    {results.length === 0 ? (
                        <div style={{ padding: "18px 14px", color: "var(--ssw-text-2, #66708a)", fontSize: 13.5 }}>
                            Nothing matches “{query}”.
                        </div>
                    ) : results.map((r, idx) => (
                        <React.Fragment key={r.person ? `person-${r.person.empId}` : `${r.to}-${idx}`}>
                        {r.person && (idx === 0 || !results[idx - 1].person) ? (
                            <div style={{ padding: "8px 12px 4px", fontSize: 11, fontWeight: 700, letterSpacing: ".5px",
                                          textTransform: "uppercase", color: "var(--ssw-text-2, #66708a)" }}>
                                People
                            </div>
                        ) : null}
                        <div
                            onMouseEnter={() => setCursor(idx)}
                            onMouseDown={(e) => { e.preventDefault(); go(r); }}
                            style={{
                                display: "flex", alignItems: "center", gap: 12,
                                padding: "10px 12px", borderRadius: 9, cursor: "pointer",
                                background: idx === cursor ? "rgba(91, 80, 214, 0.10)" : "transparent",
                            }}
                        >
                            <i className={r.icon || "ri-arrow-right-line"} aria-hidden="true"
                               style={{ fontSize: 17, color: idx === cursor ? "var(--ssw-accent-ink, #4338ca)" : "var(--ssw-text-2, #66708a)", flexShrink: 0 }} />
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ssw-text-1, #1c2333)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {r.label}
                                </div>
                                {r.hint ? (
                                    <div style={{ fontSize: 12, color: "var(--ssw-text-2, #66708a)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {r.hint}
                                    </div>
                                ) : null}
                            </div>
                            {idx === cursor ? (
                                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--ssw-text-2, #66708a)" }}>↵</span>
                            ) : null}
                        </div>
                        </React.Fragment>
                    ))}
                </div>
                )}
            </div>
        </div>
    );
};

export default CommandPalette;
